import { useCallback, useEffect, useRef } from 'react';
import { getFuseTag } from 'utilities/fuse.js';
import { IAdvertFuseProps } from '../types.js';
import { AdvertDisplayHook } from './fuse-ad-show-hook.js';
import { AdQuery } from './fuse-log.js';

/**
 * Manages callbacks from onSlotRendered
 * We need to cancel when this is unmounted
 */
export const FuseAdCallbackHook = ({
  fuseTag,
  adDisplayTrueCB,
  logger,
}: Pick<IAdvertFuseProps, 'fuseTag' | 'adUnitPath' | 'adDisplayTrueCB'> & {
  logger: (type: AdQuery) => void;
}) => {
  const displayAdCB = AdvertDisplayHook({ adDisplayTrueCB });

  /**
   * Once we pass these callbacks to fuse we cannot cancel them so we need to add a useRef that we can cancel
   * This makes sure that when they run they do nothing
   * We use isMounted to cancel these
   */
  const isMounted = useRef(true);

  /** When dom node closes remove cb reference */
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fuseCallback = useCallback(
    (cb: Publift.SlotRenderEndedEvent, fuseTagID: string) => {
      const { slotId, hasCreative: hasAd } = cb;
      if (isMounted.current && slotId.match(new RegExp(fuseTagID))) {
        /** Currently getting some false positives as there are pages AKL 7 days with 2 slots with same fuseID */
        if (hasAd) {
          displayAdCB();
        }
        logger(hasAd ? 'Ad Response Has Ad' : 'Ad Response No Ad');
      }
    },
    [displayAdCB, isMounted, logger],
  );

  useEffect(() => {
    if (fuseTag) {
      const fusetagLib = getFuseTag();
      fusetagLib.que.push(() => {
        /**
         * Running after ad slots have been rendered
         * http://showroom.publift.com/fuse/documentation.html
         * https://docs.publift.com/
         */
        fusetagLib.onSlotRenderEnded((cb) => fuseCallback(cb, fuseTag));
      });
    }
  }, [fuseCallback, fuseTag]);
};
